/**
 * @file src/sync/trackSettingsCommit.js
 * @description The ONE commit seam for a user's per-track sync-setting edits (tempo, grid marker,
 * meter): apply to the owning voice's in-memory model, then persist voice-scoped. Every UI surface
 * (React controls, the header BPM bridge) converges here — there is deliberately no second path that
 * both applies and persists one of these values.
 */
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { syncCoordinator } from './SyncCoordinator.js';
import { deckFor } from '../voice/Deck.js';
import { normalizeMeterId } from './meter.js';
import { queueTrackSyncSettingsSave } from './trackUserSettingsPersistence.js';

/** The edited voice's engine — a SPECIFIC voice when scoped (a collection tile), else the active
 *  (focused) voice for the single-orbiter case. Exported: the one UI→engine resolution seam. */
export function resolveEngine(voiceId = null) {
  return voiceId != null
    ? (voiceRegistry.get(voiceId)?.audioEngine ?? null)
    : (voiceRegistry.getActive()?.audioEngine ?? null);
}

/** The deck being edited — a SPECIFIC voice when scoped, else the active one. Mirrors
 *  `resolveEngine`'s rule so the value we persist comes from the same voice we applied it to. */
function resolveDeck(voiceId = null) {
  return voiceId != null
    ? (voiceRegistry.get(voiceId)?.deck ?? null)
    : (voiceRegistry.getActive()?.deck ?? null);
}

/** This user's saved warp CHOICE for the track, re-sent on every save so a wholesale replace can't
 *  drop it. Null means nobody ever chose, so there is nothing to preserve and omitting it deletes
 *  nothing (the deck seeds this from what was stored, so a stored value is never null here).
 *
 *  Deliberately the deck's PREFERENCE, not its live `warp`. The live flag moves for reasons that are
 *  not preferences — the mobile speed lock forces it off, a missing tempo locks it off, and in a room
 *  a peer's toggle moves it — and re-sending any of those would quietly rewrite the user's choice on
 *  the next unrelated save: a phone with the speed lock engaged would persist "warp off" and win on
 *  desktop, and a peer's toggle would become yours.
 *  @returns {boolean|null} */
function readPersistableWarp(voiceId = null) {
  return resolveDeck(voiceId)?.warpPreference ?? null;
}

/** The track's meter, re-sent on every save for the same reason as warp and the loop: the backend
 *  replaces a track's stored `sync` wholesale, so a save that omits the meter deletes it. Read from
 *  the deck, which is the meter's owner.
 *  @returns {string|null} */
function readPersistableMeter(voiceId = null) {
  const meter = resolveDeck(voiceId)?.meter;
  return typeof meter === 'string' && meter ? meter : null;
}

/** The loop this voice LOGICALLY has, in storage's units — read from the engine, never from what a
 *  caller asked for. In a room a loop edit this client doesn't hold the slot for is silently refused,
 *  so the requested value and the applied one can differ; only the applied one may be saved.
 *
 *  `getSharedLoopState()` is the engine's own "the loop I have" read: it holds a loop the playhead
 *  has merely left, and returns null only when there is genuinely no range. Milliseconds there,
 *  seconds here — storage carries one time unit, matching the grid marker.
 *  @returns {{loopEnabled: boolean|null, loopStartSec: number|null, loopEndSec: number|null}} */
function readPersistableLoop(voiceId = null) {
  const engine = resolveEngine(voiceId);
  if (!engine || typeof engine.getSharedLoopState !== 'function') {
    return { loopEnabled: null, loopStartSec: null, loopEndSec: null };
  }

  const state = engine.getSharedLoopState();
  if (!state) {
    // No range at all (never drawn, or just cleared). Loop-on without a region is inert in the
    // engine, so the honest thing to store is "off, and no region" — which also lets a cleared loop
    // erase the one that was saved before, instead of leaving a ghost to be restored next load.
    return { loopEnabled: false, loopStartSec: null, loopEndSec: null };
  }

  return {
    loopEnabled: state.enabled === true,
    loopStartSec: state.startMs / 1000,
    loopEndSec: state.endMs / 1000,
  };
}

/** Persist a track's sync settings (tempo + grid marker + warp) via the existing per-user PUT
 *  (`me/users/configurations/track-settings`). This only writes the per-user LAYER; the backend
 *  decides whether the user is the owner (→ promotes to the track's canonical `effectiveSettings`)
 *  or not (→ a personal override). The load-side priority in the shared `clock/track-metadata` resolver then resolves
 *  canonical > per-user > auto-detected — so "only the owner edits permanently" is a backend +
 *  load-priority property, NOT a frontend save gate. Reads the unchanged field from the live model.
 *  @param {string|null} [voiceId] target THIS voice's own track (a specific collection tile) instead
 *   of "whichever voice is active" — needed so editing a non-focused tile persists to ITS track. */
export function saveActiveTrackSyncSettings({
  trackBpm = null,
  gridMarkerTimeSec = null,
  meter = null,
  warpEnabled = null,
} = {}, voiceId = null) {
  const orbiter = resolveEngine(voiceId);
  const track = orbiter?.trackData?.track;
  if (!track?.trackId) return Promise.resolve(null);
  // Read live, for the same reason warp is: the backend replaces a track's stored settings wholesale,
  // so a tempo commit that omitted the loop would delete it rather than leave it alone.
  const loop = readPersistableLoop(voiceId);
  // When scoped to a SPECIFIC voice (voiceId given), an omitted trackBpm falls back to THAT voice's
  // own native tempo — and NEVER to the shared singleton, which holds whichever track last wrote it.
  // The singleton fallback here is how another track's tempo got PERSISTED into this track's saved
  // settings (a meter-only commit on a track with no tempo metadata silently saved a sibling's BPM,
  // poisoning the stored value so the leak survived every in-memory fix). Own unknown → omit.
  const ownTrackBpm = voiceId != null ? orbiter?.getOwnTrackBpm?.() : null;
  return queueTrackSyncSettingsSave({
    trackId: track.trackId,
    // Whether THIS voice's saved settings were actually read. A commit replaces the track's stored
    // block wholesale, so it may only be built on a layer somebody read — and the voice being edited
    // is the one that knows. Without it the guard falls back to asking every voice holding the track
    // to agree, which refuses a save on a good voice because a sibling's read failed.
    trackUserSettingsOutcome: orbiter?.trackData?.trackUserSettingsOutcome ?? null,
    trackBpm: trackBpm
      ?? (voiceId != null
        ? ownTrackBpm
        : (syncCoordinator.trackBpm ?? syncCoordinator.detectedTrackBpm))
      ?? null,
    gridMarkerTimeSec:
      gridMarkerTimeSec ??
      orbiter?.getGridMarkerTimeSec?.() ??
      orbiter?.getWrapGridStartTimeSec?.() ??
      0,
    // Re-sent like everything else here. It used to be written only by a meter commit, which was
    // survivable while saves were rare — but the backend replaces a track's stored `sync` wholesale,
    // so every tempo or downbeat commit was already deleting a saved time signature, and persisting
    // the loop turns that from a rare write into one per loop drag. Read from the deck when the
    // caller isn't the meter commit itself.
    meter: meter ?? readPersistableMeter(voiceId),
    // Warp is RE-SENT on every save, like tempo and the grid marker above and unlike meter, because
    // the backend replaces a track's stored settings wholesale rather than merging field by field.
    // Omitting it on a tempo commit would therefore not "leave it alone" — it would delete the
    // user's saved warp choice. Read from the deck (the owner of the live value), and only once the
    // deck actually has a tempo: while it does not, warp is forced off for a reason that has nothing
    // to do with preference, and persisting that would overwrite a real choice with a side effect.
    warpEnabled: warpEnabled ?? readPersistableWarp(voiceId),
    loopEnabled: loop.loopEnabled,
    loopStartSec: loop.loopStartSec,
    loopEndSec: loop.loopEndSec,
  }).catch((error) => {
    console.warn('[TrackSettings] Failed to save track sync settings.', error);
    return null;
  });
}

/** Commit a user BPM edit from the React control: update the track tempo AND persist.
 *  @param {number} bpm
 *  @param {string|null} [voiceId] the editing voice (a collection tile); omit for single-orbiter.
 *
 *  A track-BPM edit IS the editing voice's new NATIVE tempo (its grid reference — the persisted
 *  value), written on that voice's own deck; a deck's follow ratio is always transport / OWN native,
 *  so the shared fan can never retune a sibling's native. The shared singleton is refreshed only by
 *  a SYNCED tile (or single-orbiter) — it serves persist fallback + solo readers; an unsynced
 *  tile's edit stays local to its own deck. */
export function commitTrackBpmFromUi(bpm, voiceId = null) {
  const value = Number(bpm);
  if (!Number.isFinite(value) || value <= 0) return;
  const orbiter = resolveEngine(voiceId);
  // The deck is the gate: in a room the native tempo is shared state, so a deck this client does not
  // hold REFUSES the write and says so. Fanning to the shared singleton or persisting past a refusal
  // would save a per-user override for a value the app never applied — and MIDI reaches this seam even
  // where the pointer cover does not.
  if (orbiter?.setOwnTrackBpm?.(value) === false) return;
  if (voiceId == null || deckFor(voiceId)?.syncEnabled === true) {
    syncCoordinator.setTrackBpm(value, { source: 'ui' });
  }
  saveActiveTrackSyncSettings({ trackBpm: value }, voiceId);
}

/** Commit a user grid-marker pick from the React control (marker already set on the view): persist
 *  to the EDITING voice's own track — not "whichever voice is focused", the same cross-voice
 *  persistence class fixed for tempo and meter.
 *
 *  Only reached when the APPLY was permitted: in a room the downbeat is shared state, so a deck this
 *  client does not hold refuses it, and the caller drops the commit rather than save an override for a
 *  marker the app never moved.
 *  @param {number|null} [gridMarkerTimeSec]
 *  @param {string|null} [voiceId] the editing voice (a collection tile); omit for single-orbiter. */
export function commitGridMarkerFromUi(gridMarkerTimeSec = null, voiceId = null) {
  saveActiveTrackSyncSettings({ gridMarkerTimeSec }, voiceId);
}

/** Commit a user WARP toggle from the React control (or MIDI, which reaches the same seam): apply to
 *  the editing voice's deck AND persist, so the next time THIS user loads THIS track it comes back.
 *
 *  Persist only what was actually applied. `setWarp` refuses a turn-on while the mobile speed lock
 *  holds or while the track has no tempo, and in a room it refuses a toggle this client does not hold
 *  the slot for — in every one of those cases the deck keeps its old value, and writing the requested
 *  one would save a preference the app visibly did not honour. Re-reading the deck after the call is
 *  what makes a refusal a no-op here rather than a silent divergence between screen and storage.
 *
 *  A remote peer's toggle arrives at `Deck.setWarp({ roomRemote: true })` and never comes through
 *  here, so someone else's choice in a room is never saved as yours.
 *
 *  @param {boolean} enabled the requested warp state
 *  @param {string|null} [voiceId] the editing voice (a collection tile); omit for single-orbiter. */
export function commitWarpFromUi(enabled, voiceId = null) {
  const deck = resolveDeck(voiceId);
  if (!deck) return;

  const requested = enabled === true;
  deck.setWarp(requested);
  if (deck.warp !== requested) return; // refused — nothing was applied, so nothing to persist

  saveActiveTrackSyncSettings({ warpEnabled: requested }, voiceId);
}

// The document event every loop-engaged change is announced on — the transport button and MIDI
// (source 'transport'), the kit Playback panel's drag / in / out / size / clear (source 'waveform'),
// and a room peer's change re-fanned by the room client (source 'remote'). Listened to by literal,
// the way `roomClient` and the usage-events recorder already do; the canonical definition lives in
// `react/engine/createEngineContext.ts`, which stays free of app imports and so cannot own this.
const LOOP_TOGGLE_EVENT = 'ui:loop-toggle';

/**
 * Start remembering this user's loop for the track they are on.
 *
 * ONE listener rather than a call at each of the seven places a loop can move: every local path
 * already converges on this event, so subscribing here cannot miss one, and no future loop control
 * has to remember to persist. `source: 'remote'` is the room re-fanning a PEER's change — excluded,
 * or another person's loop would be written into your settings for the track.
 *
 * @returns {() => void} unsubscribe
 */
export function initTrackLoopPersistence() {
  if (typeof document === 'undefined') return () => {};

  const onLoopToggle = (event) => {
    const detail = event?.detail;
    if (detail?.source === 'remote') return; // someone else's loop, not this user's preference
    commitLoopFromUi(detail?.voiceId ?? null);
  };

  document.addEventListener(LOOP_TOGGLE_EVENT, onLoopToggle);
  return () => document.removeEventListener(LOOP_TOGGLE_EVENT, onLoopToggle);
}

/** Persist the voice's CURRENT loop — on/off and the region — after a local loop edit.
 *
 *  Deliberately takes no description of the change: it reads the engine's resulting loop instead, so
 *  every path that can move it (the transport button, MIDI, the region drag, loop in/out, a size
 *  preset, clear) persists through this one call without threading values, and an edit the room
 *  refused saves the unchanged truth rather than the rejected request.
 *
 *  A remote peer's loop arrives through the room bridge and never reaches this seam, so it is never
 *  saved as yours. Saves are debounced per track downstream, which is what makes it safe to call on
 *  every frame of a loop drag.
 *
 *  @param {string|null} [voiceId] the editing voice (a collection tile); omit for single-orbiter. */
export function commitLoopFromUi(voiceId = null) {
  saveActiveTrackSyncSettings({}, voiceId);
}

/** Apply a meter (time signature) value for a SPECIFIC voice (a collection tile), in-memory only —
 *  shared by the live (drag) and commit paths below. Meter is ALWAYS per-voice (a property of the
 *  track, never shared — even between two synced voices), so the edit goes straight to that voice's own
 *  deck (`Deck.setMeter`); there is no shared-meter write path to leak through.
 *  @param {string} meter
 *  @param {string|null} [voiceId] the editing voice; omit for the single-orbiter/no-collection case.
 *  @returns {string} the normalized meter id that was applied. */
function applyTrackMeterFromUi(meter, voiceId = null) {
  const normalized = normalizeMeterId(meter);
  // Scoped to a SPECIFIC voice (voiceId given): target ONLY that voice's deck — never fall back to
  // whichever voice is active, so the live apply targets the same track the persist step does (see
  // saveActiveTrackSyncSettings). Single-orbiter (voiceId null) uses the active voice's deck.
  (voiceId != null ? voiceRegistry.get(voiceId)?.deck : voiceRegistry.getActive()?.deck)?.setMeter(normalized);
  return normalized;
}

/** Live (drag-in-progress) meter update for a specific voice: apply only, no persistence.
 *  @param {string} meter
 *  @param {string|null} [voiceId] */
export function setTrackMeterLiveFromUi(meter, voiceId = null) {
  applyTrackMeterFromUi(meter, voiceId);
}

/** Commit a user meter pick from the React control for a specific voice: apply AND persist
 *  per-track, the same way tempo is saved/loaded.
 *  @param {string} meter
 *  @param {string|null} [voiceId] */
export function commitTrackMeterFromUi(meter, voiceId = null) {
  const normalized = applyTrackMeterFromUi(meter, voiceId);
  saveActiveTrackSyncSettings({ meter: normalized }, voiceId);
}
